/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useText } from 'preact-i18n';
import { ValidationErrorTypes } from '@/auth/types';

/**
 * A custom hook for handling translations with support for extensible error messages.
 *
 * @param defaultKeys - A mapping of keys to their translation paths.
 * @returns A complete set of translations, including dynamically updated values for null error keys.
 */
export const useCustomTranslations = (
  defaultKeys: Record<string, string>
): Record<string, string> => {
  /**
   * Step 1: Fetch initial translations using the provided keys
   * Translations are fetched from the i18n system based on the provided defaultKeys.
   */
  const translations = useText(defaultKeys);

  /**
   * Step 2: Identify keys that require updates
   * - These keys correspond to validation errors (defined in ValidationErrorTypes).
   * - A key needs an update if:
   *   1. Its value in the initial translations is `null`.
   *   2. The key exists in ValidationErrorTypes (indicating it’s a known error type).
   *
   * Null values typically occur when translations for the given error key are not overridden
   * and instead rely on a plain structure, e.g., "requiredFieldError": "This is a required field."
   */
  const keysToUpdate = Object.entries(translations)
    .filter(
      ([key, value]) =>
        value === null &&
        (Object.values(ValidationErrorTypes) as string[]).includes(key)
    )
    .map(([key]) => key);

  /**
   * Step 3: Prepare paths for fetching updated translations
   * - For each key needing an update, create a path to its extended translation.
   * - Example: For the key "requiredFieldError", the updated path would be "Auth.FormText.requiredFieldError".
   */
  const translationKeys = keysToUpdate.reduce((acc, key) => {
    acc[key] = `Auth.FormText.${key}`; // Build the updated translation path
    return acc;
  }, {} as Record<string, string>);

  // Step 4: Fetch updated translations for the keys identified in Step 2
  const updatedTranslations = useText(translationKeys);

  /**
   * Step 5: Merge updated translations back into the initial translations
   * - Replace `null` values with the fetched updated translations.
   * - Preserve all other original translations.
   */
  return {
    ...translations, // Include original translations
    ...keysToUpdate.reduce<Record<string, string>>((acc, key) => {
      acc[key] = updatedTranslations[key]; // Overwrite `null` values with updated translations
      return acc;
    }, {}),
  };
};
